/**
 * De-duplication for the checkout-funnel analytics events (`begin_checkout`, `view_item_list`,
 * `add_to_cart`, `add_payment_info`).
 *
 * Each of those is fired from a `useEffect` guarded by a `useRef`, which only survives while the
 * component is mounted. The booking wizard renders one step at a time (`BookingWizard.tsx`
 * renders `tabs[currentTabIndex].component`), so moving between steps unmounts the step being
 * left and resets its ref. Navigating cart → payment → cart therefore pushed a second
 * `add_to_cart` and a second `add_payment_info`, inflating the operator's funnel and — because
 * `add_to_cart` reports the whole cart rather than a delta — its cart value and item quantities
 * along with it.
 *
 * The marker has to outlive the component but NOT the browsing session: a new tab is a new
 * checkout and should fire the funnel again. That makes `sessionStorage` the right home, which is
 * also where the wizard keeps its transaction id.
 *
 * ## What counts as "the same checkout"
 *
 * The obvious key would be the transaction id, but two of these events fire before a transaction
 * exists — `begin_checkout` when the wizard finishes loading and `view_item_list` at the
 * trip-selection step. So the record stores the transaction id it was last seen under and infers
 * the boundary from how that id changes:
 *
 * | Stored  | Current | Meaning                                   | Action       |
 * |---------|---------|-------------------------------------------|--------------|
 * | null    | null    | still before the reservation               | keep         |
 * | null    | T       | same checkout, reservation just created    | keep, adopt T|
 * | T       | T       | same checkout                              | keep         |
 * | T       | null    | checkout ended (paid, expired, abandoned)  | reset        |
 * | T       | U       | a different checkout                       | reset        |
 *
 * Holding the markers across the null → T transition is what stops a step revisit from re-firing
 * once the reservation exists; resetting on T → null is what lets a genuine second booking in the
 * same tab report its funnel in full. Every one of the ~20 call sites that ends a checkout goes
 * through `clearFerryBookingTransactionIdFromStorage`, so the T → null transition is reliable and
 * nothing has to notify this module.
 *
 * `purchase` is deliberately not handled here: it needs to survive the session (the confirmation
 * page can be re-opened from the confirmation email in a new tab) and so lives in
 * `purchaseDedupe.ts`, backed by `localStorage`.
 */

import { readRaw, writeRaw } from './analyticsStorage';

const STORAGE_KEY = 'fh-analytics-tracked-funnel';

/**
 * Read directly rather than importing `getFerryBookingTransactionIdFromStorage`, matching what
 * `analyticsEvents.ts` already does. It keeps the analytics utilities free of a dependency on the
 * booking service, which imports axios and the API layer.
 */
const TRANSACTION_ID_KEY = 'ferry-booking-transaction-id-key';

interface FunnelRecord {
	/** The transaction id these markers were recorded under, or null if none existed yet. */
	transactionId: string | null;
	/** Event names already pushed for this checkout. */
	events: string[];
}

const EMPTY: FunnelRecord = { transactionId: null, events: [] };

function currentTransactionId(): string | null {
	return readRaw('sessionStorage', TRANSACTION_ID_KEY);
}

function parse(raw: string | null): FunnelRecord {
	if (!raw) {
		return EMPTY;
	}
	try {
		const parsed = JSON.parse(raw);
		if (parsed === null || typeof parsed !== 'object') {
			return EMPTY;
		}
		const { transactionId, events } = parsed as Partial<FunnelRecord>;
		return {
			transactionId: typeof transactionId === 'string' ? transactionId : null,
			events: Array.isArray(events) ? events.filter((x): x is string => typeof x === 'string') : [],
		};
	} catch {
		// Malformed or truncated — treat as nothing recorded rather than throwing.
		return EMPTY;
	}
}

/**
 * The record for the checkout in progress, with markers from a previous checkout discarded.
 * See the table above for which transitions reset and which carry over.
 */
function readForCurrentCheckout(): FunnelRecord {
	const stored = parse(readRaw('sessionStorage', STORAGE_KEY));
	const current = currentTransactionId();

	if (stored.transactionId === null) {
		// Either still pre-reservation, or the reservation was created part-way through this same
		// checkout. Both keep the markers; the second adopts the new id.
		return { transactionId: current, events: stored.events };
	}
	if (stored.transactionId === current) {
		return stored;
	}
	// The stored id was cleared or replaced, so this is a different checkout.
	return { transactionId: current, events: [] };
}

/** True when this funnel event has already been pushed for the checkout in progress. */
export function funnelEventAlreadyTracked(event: string): boolean {
	if (!event) {
		return false;
	}
	return readForCurrentCheckout().events.includes(event);
}

/** Record that this funnel event has been pushed, so revisiting its step will not repeat it. */
export function markFunnelEventTracked(event: string): void {
	if (!event) {
		return;
	}
	const record = readForCurrentCheckout();
	if (record.events.includes(event)) {
		// Still write, so a transaction id adopted by `readForCurrentCheckout` is persisted.
		writeRaw('sessionStorage', STORAGE_KEY, JSON.stringify(record));
		return;
	}
	const next: FunnelRecord = {
		transactionId: record.transactionId,
		events: [...record.events, event],
	};
	writeRaw('sessionStorage', STORAGE_KEY, JSON.stringify(next));
}
